import React, { useState, useEffect } from 'react';
import { HardDrive, Check, Info } from 'lucide-react';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

const CameraStorageOptions = ({ onStorageChange, productCategory }) => {
  const [storageCards, setStorageCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOption, setSelectedOption] = useState('none'); // 'none', '128', '256'

  useEffect(() => {
    const fetchStorageCards = async () => {
      try {
        const response = await fetch(`${API}/products?category=video_basic`);
        if (response.ok) {
          const data = await response.json();
          // Filter only storage cards (MicroSD)
          const cards = data.filter(p => 
            p.subcategory === 'storage' || 
            p.name.toLowerCase().includes('microsd') ||
            p.name.toLowerCase().includes('carte')
          );
          setStorageCards(cards);
        }
      } catch (error) {
        console.error('Failed to fetch storage cards:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStorageCards();
  }, []);

  const handleOptionChange = (option) => {
    setSelectedOption(option);
    
    if (option === 'none') {
      onStorageChange && onStorageChange(null, 0);
    } else {
      const card = storageCards.find(c => c.name.includes(option === '128' ? '128' : '256'));
      if (card) {
        onStorageChange && onStorageChange({
          product_id: card.product_id,
          name: card.name,
          price: card.price,
          quantity: 1
        }, card.price);
      }
    }
  };

  const get128Card = () => storageCards.find(c => c.name.includes('128'));
  const get256Card = () => storageCards.find(c => c.name.includes('256'));

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-8 bg-zinc-800 rounded w-1/3" />
        <div className="h-24 bg-zinc-800 rounded" />
      </div>
    );
  }

  if (storageCards.length === 0) return null;

  const card128 = get128Card();
  const card256 = get256Card();

  return (
    <div className="space-y-4" data-testid="camera-storage-options">
      <div className="flex items-center gap-2">
        <HardDrive className="w-4 h-4 text-emerald-400" />
        <h4 className="text-sm font-semibold text-white">
          Carte de stockage
        </h4>
        <span className="text-xs text-white/50">(optionnel)</span>
      </div>

      <div className="p-3 bg-blue-500/5 border border-blue-500/20 rounded-lg mb-3">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-white/70">
            Une carte MicroSD permet d'enregistrer directement sur la caméra sans NVR. 
            Idéal pour un enregistrement autonome et une sauvegarde locale.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        {/* Option: Sans carte */}
        <label 
          className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
            selectedOption === 'none'
              ? 'bg-zinc-800/50 border-zinc-600'
              : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              selectedOption === 'none' 
                ? 'border-emerald-500 bg-emerald-500' 
                : 'border-zinc-600'
            }`}>
              {selectedOption === 'none' && <Check className="w-3 h-3 text-white" />}
            </div>
            <div>
              <p className="text-sm font-medium text-white">Sans carte de stockage</p>
              <p className="text-xs text-white/50">Enregistrement via NVR ou cloud uniquement</p>
            </div>
          </div>
          <span className="text-sm font-bold text-white/70">Inclus</span>
          <input 
            type="radio" 
            name="storage" 
            value="none" 
            checked={selectedOption === 'none'}
            onChange={() => handleOptionChange('none')}
            className="sr-only"
          />
        </label>

        {/* Option: 128 Go */}
        {card128 && (
          <label 
            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
              selectedOption === '128'
                ? 'bg-emerald-500/10 border-emerald-500/30'
                : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedOption === '128' 
                  ? 'border-emerald-500 bg-emerald-500' 
                  : 'border-zinc-600'
              }`}>
                {selectedOption === '128' && <Check className="w-3 h-3 text-white" />}
              </div>
              <div>
                <p className="text-sm font-medium text-white">Carte MicroSD 128 Go</p>
                <p className="text-xs text-white/50">~7 jours d'enregistrement en 4K</p>
              </div>
            </div>
            <span className="text-sm font-bold text-emerald-400">+{card128.price.toFixed(2)}€</span>
            <input 
              type="radio" 
              name="storage" 
              value="128" 
              checked={selectedOption === '128'}
              onChange={() => handleOptionChange('128')}
              className="sr-only"
            />
          </label>
        )}

        {/* Option: 256 Go */}
        {card256 && (
          <label 
            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
              selectedOption === '256'
                ? 'bg-emerald-500/10 border-emerald-500/30'
                : 'bg-zinc-900/50 border-zinc-800 hover:border-zinc-700'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selectedOption === '256' 
                  ? 'border-emerald-500 bg-emerald-500' 
                  : 'border-zinc-600'
              }`}>
                {selectedOption === '256' && <Check className="w-3 h-3 text-white" />}
              </div>
              <div className="flex items-center gap-2">
                <div>
                  <p className="text-sm font-medium text-white">Carte MicroSD 256 Go</p>
                  <p className="text-xs text-white/50">~14 jours d'enregistrement en 4K</p>
                </div>
                <span className="px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] font-bold rounded">
                  RECOMMANDÉ
                </span>
              </div>
            </div>
            <span className="text-sm font-bold text-emerald-400">+{card256.price.toFixed(2)}€</span>
            <input 
              type="radio" 
              name="storage" 
              value="256" 
              checked={selectedOption === '256'}
              onChange={() => handleOptionChange('256')}
              className="sr-only"
            />
          </label>
        )}
      </div>
    </div>
  );
};

export default CameraStorageOptions;
